import { useState, useEffect, useMemo, useCallback } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
} from "chart.js";
import { Bar, Doughnut, Line } from "react-chartjs-2";
import { TenantService } from "@/services/tenantService";
import type { PlatformStats, TenantDeploymentInfo } from "@/types/tenant";
import { TbDatabase, TbServer, TbArrowsExchange } from "react-icons/tb";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement
);

const STATUS_COLORS: Record<string, string> = {
  pending: "#f59e0b",
  active: "#3b82f6",
  in_progress: "#8b5cf6",
  completed: "#10b981",
  cancelled: "#ef4444",
  on_hold: "#6b7280",
  unknown: "#d1d5db",
};

export const PlatformAnalytics: React.FC = () => {
  const [stats, setStats] = useState<PlatformStats | null>(null);
  const [loading, setLoading] = useState(true);

  const loadStats = useCallback(async () => {
    setLoading(true);
    try {
      const data = await TenantService.getPlatformStats();
      setStats(data);
    } catch (error) {
      console.error("Error loading analytics:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  if (loading || !stats) {
    return (
      <div className="flex justify-center py-12">
        <div className="w-8 h-8 border-4 border-[#374795] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return <AnalyticsContent stats={stats} />;
};

const AnalyticsContent: React.FC<{ stats: PlatformStats }> = ({ stats }) => {
  const completedTasks = useMemo(() => {
    return stats.tasks_by_status?.completed || 0;
  }, [stats.tasks_by_status]);

  const tenantUsageData = useMemo(() => {
    const topTenants = (stats.tenant_usage || []).slice(0, 10);
    return {
      labels: topTenants.map((t) => t.tenant_name),
      datasets: [
        {
          label: "Tasks",
          data: topTenants.map((t) => t.task_count),
          backgroundColor: "#374795",
          borderRadius: 4,
        },
      ],
    };
  }, [stats.tenant_usage]);

  const userGrowthData = useMemo(() => {
    const data = stats.user_growth || [];
    return {
      labels: data.map((d) => {
        const [year, month] = d.month.split("-");
        const date = new Date(parseInt(year), parseInt(month) - 1);
        return date.toLocaleDateString("en-US", {
          month: "short",
          year: "2-digit",
        });
      }),
      datasets: [
        {
          label: "New Users",
          data: data.map((d) => d.count),
          backgroundColor: "#10b981",
          borderRadius: 4,
        },
      ],
    };
  }, [stats.user_growth]);

  const taskTrendsData = useMemo(() => {
    const data = stats.task_trends || [];
    return {
      labels: data.map((d) => {
        const [year, month] = d.month.split("-");
        const date = new Date(parseInt(year), parseInt(month) - 1);
        return date.toLocaleDateString("en-US", {
          month: "short",
          year: "2-digit",
        });
      }),
      datasets: [
        {
          label: "Created",
          data: data.map((d) => d.created),
          borderColor: "#3b82f6",
          backgroundColor: "rgba(59, 130, 246, 0.1)",
          fill: true,
          tension: 0.3,
        },
        {
          label: "Completed",
          data: data.map((d) => d.completed),
          borderColor: "#10b981",
          backgroundColor: "rgba(16, 185, 129, 0.1)",
          fill: true,
          tension: 0.3,
        },
      ],
    };
  }, [stats.task_trends]);

  const tasksByStatusData = useMemo(() => {
    const statusEntries = Object.entries(stats.tasks_by_status || {});
    return {
      labels: statusEntries.map(
        ([status]) =>
          status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, " ")
      ),
      datasets: [
        {
          data: statusEntries.map(([, count]) => count),
          backgroundColor: statusEntries.map(
            ([status]) => STATUS_COLORS[status] || "#d1d5db"
          ),
          borderWidth: 0,
        },
      ],
    };
  }, [stats.tasks_by_status]);

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
      y: { beginAtZero: true, ticks: { precision: 0 } },
    },
  };

  const horizontalBarOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: "y" as const,
    plugins: { legend: { display: false } },
    scales: {
      x: { beginAtZero: true, ticks: { precision: 0 } },
    },
  };

  const lineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { position: "top" as const } },
    scales: {
      y: { beginAtZero: true, ticks: { precision: 0 } },
    },
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { position: "right" as const } },
  };

  return (
    <div className="w-full space-y-6">
      <h2 className="text-lg font-semibold text-[#444]">Analytics</h2>

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <StatCard
          label="Total Tenants"
          value={stats.total_tenants}
          color="blue"
        />
        <StatCard
          label="Active Tenants"
          value={stats.active_tenants}
          color="green"
        />
        <StatCard
          label="Inactive Tenants"
          value={stats.inactive_tenants}
          color="red"
        />
        <StatCard
          label="Total Users"
          value={stats.total_users}
          color="indigo"
        />
        <StatCard
          label="Total Tasks"
          value={stats.total_tasks}
          color="purple"
        />
        <StatCard
          label="Completed Tasks"
          value={completedTasks}
          color="emerald"
        />
      </div>

      {/* Charts grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5">
          <h4 className="text-base font-semibold text-[#444] mb-4">
            Tasks by Tenant
          </h4>
          <div className="h-64">
            {(stats.tenant_usage || []).length > 0 ? (
              <Bar data={tenantUsageData} options={horizontalBarOptions} />
            ) : (
              <EmptyChart />
            )}
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5">
          <h4 className="text-base font-semibold text-[#444] mb-4">
            Tasks by Status
          </h4>
          <div className="h-64">
            {Object.keys(stats.tasks_by_status || {}).length > 0 ? (
              <Doughnut data={tasksByStatusData} options={doughnutOptions} />
            ) : (
              <EmptyChart />
            )}
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5">
          <h4 className="text-base font-semibold text-[#444] mb-4">
            User Growth (Last 12 Months)
          </h4>
          <div className="h-64">
            {(stats.user_growth || []).length > 0 ? (
              <Bar data={userGrowthData} options={barOptions} />
            ) : (
              <EmptyChart />
            )}
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5">
          <h4 className="text-base font-semibold text-[#444] mb-4">
            Task Trends (Last 12 Months)
          </h4>
          <div className="h-64">
            {(stats.task_trends || []).length > 0 ? (
              <Line data={taskTrendsData} options={lineOptions} />
            ) : (
              <EmptyChart />
            )}
          </div>
        </div>
      </div>

      {/* Database Deployment */}
      {stats.db_deployment && (
        <DatabaseDeploymentSection deployment={stats.db_deployment} />
      )}
    </div>
  );
};

const MIGRATION_STATUS_STYLES: Record<
  string,
  { bg: string; text: string; label: string }
> = {
  completed: { bg: "bg-green-50", text: "text-green-700", label: "Migrated" },
  in_progress: {
    bg: "bg-blue-50",
    text: "text-blue-700",
    label: "In Progress",
  },
  failed: { bg: "bg-red-50", text: "text-red-700", label: "Failed" },
  pending: { bg: "bg-yellow-50", text: "text-yellow-700", label: "Pending" },
};

const DatabaseDeploymentSection: React.FC<{
  deployment: PlatformStats["db_deployment"];
}> = ({ deployment }) => {
  const total = deployment.shared_count + deployment.dedicated_count;
  const dedicatedPct =
    total > 0 ? Math.round((deployment.dedicated_count / total) * 100) : 0;
  const sharedPct = total > 0 ? 100 - dedicatedPct : 0;

  return (
    <div className="space-y-4">
      <h3 className="text-base font-semibold text-[#444]">
        Database Deployment
      </h3>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 mb-1">
            <TbServer className="w-4 h-4 text-blue-500" />
            <p className="text-sm font-medium text-blue-500">Shared DB</p>
          </div>
          <p className="text-xl font-bold text-blue-700">
            {deployment.shared_count}
          </p>
        </div>
        <div className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 mb-1">
            <TbDatabase className="w-4 h-4 text-purple-500" />
            <p className="text-sm font-medium text-purple-500">Dedicated DB</p>
          </div>
          <p className="text-xl font-bold text-purple-700">
            {deployment.dedicated_count}
          </p>
        </div>
        {Object.entries(deployment.migrations_by_status).map(
          ([status, count]) => {
            const style = MIGRATION_STATUS_STYLES[status] || {
              bg: "bg-gray-50",
              text: "text-gray-700",
              label: status,
            };
            return (
              <div
                key={status}
                className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm"
              >
                <div className="flex items-center gap-2 mb-1">
                  <TbArrowsExchange className="w-4 h-4 text-gray-500" />
                  <p className={`text-sm font-medium ${style.text}`}>
                    {style.label}
                  </p>
                </div>
                <p className={`text-xl font-bold ${style.text}`}>{count}</p>
              </div>
            );
          }
        )}
      </div>

      {/* Distribution bar */}
      {total > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5">
          <h4 className="text-sm font-medium text-[#444] mb-3">Distribution</h4>
          <div className="flex rounded-full overflow-hidden h-6">
            {sharedPct > 0 && (
              <div
                className="bg-blue-500 flex items-center justify-center text-xs text-white font-medium"
                style={{ width: `${sharedPct}%` }}
              >
                {sharedPct > 15 && `${sharedPct}%`}
              </div>
            )}
            {dedicatedPct > 0 && (
              <div
                className="bg-purple-500 flex items-center justify-center text-xs text-white font-medium"
                style={{ width: `${dedicatedPct}%` }}
              >
                {dedicatedPct > 15 && `${dedicatedPct}%`}
              </div>
            )}
          </div>
          <div className="flex gap-4 mt-2 text-xs text-[#828282]">
            <span className="flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded-full bg-blue-500 inline-block" />
              Shared ({deployment.shared_count})
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded-full bg-purple-500 inline-block" />
              Dedicated ({deployment.dedicated_count})
            </span>
          </div>
        </div>
      )}

      {/* Per-tenant table */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5">
        <h4 className="text-sm font-medium text-[#444] mb-3">
          Tenant Database Details
        </h4>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-2 px-3 text-[#828282] font-medium">
                  Tenant
                </th>
                <th className="text-left py-2 px-3 text-[#828282] font-medium">
                  Database
                </th>
                <th className="text-left py-2 px-3 text-[#828282] font-medium">
                  Migration
                </th>
                <th className="text-left py-2 px-3 text-[#828282] font-medium">
                  Migrated At
                </th>
              </tr>
            </thead>
            <tbody>
              {deployment.tenants.map((t: TenantDeploymentInfo) => {
                const migStyle = t.migration_status
                  ? MIGRATION_STATUS_STYLES[t.migration_status] || {
                      bg: "bg-gray-50",
                      text: "text-gray-600",
                      label: t.migration_status,
                    }
                  : null;
                return (
                  <tr
                    key={t.tenant_id}
                    className="border-b border-gray-100 last:border-0"
                  >
                    <td className="py-2.5 px-3 text-[#444]">{t.tenant_name}</td>
                    <td className="py-2.5 px-3">
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                          t.db_type === "dedicated"
                            ? "bg-purple-50 text-purple-700"
                            : "bg-blue-50 text-blue-700"
                        }`}
                      >
                        {t.db_type === "dedicated" ? (
                          <TbDatabase className="w-3 h-3" />
                        ) : (
                          <TbServer className="w-3 h-3" />
                        )}
                        {t.db_type === "dedicated" ? "Dedicated" : "Shared"}
                      </span>
                    </td>
                    <td className="py-2.5 px-3">
                      {migStyle ? (
                        <span
                          className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${migStyle.bg} ${migStyle.text}`}
                        >
                          {migStyle.label}
                        </span>
                      ) : (
                        <span className="text-[#828282] text-xs">--</span>
                      )}
                    </td>
                    <td className="py-2.5 px-3 text-[#828282] text-xs">
                      {t.migrated_at
                        ? new Date(t.migrated_at).toLocaleString()
                        : "--"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const StatCard: React.FC<{ label: string; value: number; color: string }> = ({
  label,
  value,
  color,
}) => {
  const colorMap: Record<string, string> = {
    blue: "text-blue-700",
    green: "text-green-700",
    red: "text-red-700",
    indigo: "text-indigo-700",
    purple: "text-purple-700",
    emerald: "text-emerald-700",
  };
  const labelColorMap: Record<string, string> = {
    blue: "text-blue-500",
    green: "text-green-500",
    red: "text-red-500",
    indigo: "text-indigo-500",
    purple: "text-purple-500",
    emerald: "text-emerald-500",
  };

  return (
    <div className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm text-center">
      <p className={`text-xl font-bold ${colorMap[color] || "text-gray-700"}`}>
        {value}
      </p>
      <p
        className={`text-sm font-medium ${labelColorMap[color] || "text-gray-500"}`}
      >
        {label}
      </p>
    </div>
  );
};

const EmptyChart: React.FC = () => (
  <div className="flex items-center justify-center h-full text-[#828282] text-sm">
    No data available
  </div>
);
